import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import captureScreenshot from 'utils/screenshotUtils';

const ExecutiveSummary = () => {
  const reviewData = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results);
  const contentRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Check if we have the data sections
  const hasExecutiveSummary = reviewData?.executive_summary && reviewData.executive_summary.length > 0;
  const hasSuggestedActions = reviewData?.suggested_actions && reviewData.suggested_actions.length > 0;

  if (!hasExecutiveSummary && !hasSuggestedActions) {
    return null;
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadAsPng = () => {
    captureScreenshot({
      contentRef,
      filename: 'executive-summary',
      afterCapture: handleClose
    });
  };

  const convertToRTF = () => {
    let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

    // Escape RTF characters
    const escapeRTF = (text) => {
      if (typeof text !== 'string') {
        text = String(text) || '';
      }
      return text.replace(/\\/g, '\\\\').replace(/{/g, '\\{').replace(/}/g, '\\}');
    };

    // Add title
    rtf += '\\b\\fs24 Executive Summary\\b0\\fs20\\par\\par\n';

    // Add Executive Summary section
    if (hasExecutiveSummary) {
      rtf += '\\b\\fs20 Key Insights:\\b0\\fs16\\par\\par\n';
      reviewData.executive_summary.forEach((insight) => {
        rtf += `• ${escapeRTF(insight)}\\par\n`;
      });
      rtf += '\\par\n';
    }

    // Add Suggested Actions section
    if (hasSuggestedActions) {
      rtf += '\\b\\fs20 Recommended Actions:\\b0\\fs16\\par\\par\n';
      reviewData.suggested_actions.forEach((action) => {
        rtf += `• ${escapeRTF(action)}\\par\n`;
      });
      rtf += '\\par\n';
    }

    rtf += '}';
    return rtf;
  };

  const downloadAsDoc = () => {
    const rtfContent = convertToRTF();
    const blob = new Blob([rtfContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'executive-summary.doc';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleClose();
  };

  return (
    <Box sx={{ mb: 4 }} ref={contentRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
        >
          Executive Insights
        </Typography>
        <Box className="screenshot-controls">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ height: 28, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: {
                border: 1,
                borderColor: 'secondary.light',
                borderRadius: 1
              }
            }}
          >
            <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
            <MenuItem onClick={downloadAsDoc}>Download as DOC</MenuItem>
          </Menu>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Key Insights */}
        {hasExecutiveSummary && (
          <Grid item xs={12} md={6}>
            <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  position: 'relative',
                  isolation: 'isolate',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    padding: '1px',
                    borderRadius: 'inherit',
                    background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    zIndex: -1
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                  },
                  bgcolor: 'background.paper'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: 'primary.main'
                    }}
                  >
                    Key Insights
                  </Typography>

                  <List sx={{ pl: 0 }}>
                    {reviewData.executive_summary.map((insight, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          py: 0.75,
                          px: 0,
                          '&:not(:last-child)': {
                            borderBottom: '1px dashed',
                            borderColor: 'divider'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <ArrowRightAltIcon sx={{ color: 'primary.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={<Typography variant="body1">{insight}</Typography>} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        )}

        {/* Recommended Actions */}
        {hasSuggestedActions && (
          <Grid item xs={12} md={6}>
            <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  position: 'relative',
                  isolation: 'isolate',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    padding: '1px',
                    borderRadius: 'inherit',
                    background: 'linear-gradient(135deg, rgba(255,107,107,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    zIndex: -1
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                  },
                  bgcolor: 'background.paper'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent'
                    }}
                  >
                    Recommended Actions
                  </Typography>

                  <List sx={{ pl: 0 }}>
                    {reviewData.suggested_actions.map((action, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          py: 0.75,
                          px: 0,
                          '&:not(:last-child)': {
                            borderBottom: '1px dashed',
                            borderColor: 'divider'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <LightbulbOutlinedIcon sx={{ color: 'secondary.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={<Typography variant="body1">{action}</Typography>} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ExecutiveSummary;
